import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Download, Copy, BookOpen, FileText, Clock, CheckCircle, AlertCircle, RotateCcw, Play, Eye, EyeOff } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { calculateWordCount, formatProgress, estimateRemainingTime, parseMarkdownContent } from './novelUtils'

const NovelResultPage = ({ 
  query, 
  outline, 
  novelConfig,
  generatedContent, 
  currentSection, 
  isGenerating, 
  progress, 
  onBack, 
  onContinueGeneration, 
  onRegenerateSection,
  error,
  startTime
}) => {
  const [copiedSection, setCopiedSection] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [selectedSection, setSelectedSection] = useState(null)

  // Calculate statistics
  const totalSections = outline?.length || 0
  const generatedContentArray = generatedContent ? Object.values(generatedContent) : []
  const completedSections = generatedContentArray.length
  const totalWords = generatedContentArray.reduce((sum, content) => sum + calculateWordCount(content), 0) || 0
  const progressPercentage = totalSections > 0 ? (completedSections / totalSections) * 100 : 0
  const estimatedTotalWords = outline?.reduce((sum, section) => sum + ((section.minWords || 8000) + (section.maxWords || 15000)) / 2, 0) || 0
  const remainingTime = startTime ? estimateRemainingTime(completedSections, totalSections, startTime) : 'Belum dimulai'

  // Handle copy to clipboard
  const handleCopySection = async (sectionId, content) => {
    try {
      await navigator.clipboard.writeText(content)
      setCopiedSection(sectionId)
      setTimeout(() => setCopiedSection(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleCopyAll = async () => {
    try {
      const fullContent = outline
        .filter(section => generatedContent[section.id])
        .map(section => `# ${section.title}\n\n${generatedContent[section.id]}`)
        .join('\n\n---\n\n')
      
      await navigator.clipboard.writeText(fullContent)
      setCopiedSection('all')
      setTimeout(() => setCopiedSection(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Split content into pages with max 250-300 words per page
  const splitIntoPages = (content, maxWordsPerPage = 275) => {
    const words = content.split(/\s+/)
    const pages = []
    
    for (let i = 0; i < words.length; i += maxWordsPerPage) {
      const pageWords = words.slice(i, i + maxWordsPerPage)
      pages.push(pageWords.join(' '))
    }
    
    return pages
  }

  // Handle download as .doc file with rich text format
  const handleDownload = () => {
    // Create professional RTF document with world-class formatting
    let rtfContent = '{\\rtf1\\ansi\\deff0'
    
    // Font table with professional typography
    rtfContent += '{\\fonttbl'
    rtfContent += '{\\f0\\froman\\fcharset0 Times New Roman;}' // Main text font - classic serif for readability
    rtfContent += '{\\f1\\froman\\fcharset0 Garamond;}' // Title font - elegant serif
    rtfContent += '{\\f2\\fswiss\\fcharset0 Arial;}' // Sans-serif for modern elements
    rtfContent += '}'
    
    // Color table for subtle visual hierarchy
    rtfContent += '{\\colortbl;\\red0\\green0\\blue0;\\red32\\green32\\blue32;\\red64\\green64\\blue64;}'
    
    // Document settings for professional layout
    rtfContent += '\\viewkind4\\uc1\\pard'
    rtfContent += '\\paperw12240\\paperh15840' // A4 paper size
    rtfContent += '\\margl1440\\margr1440\\margt1440\\margb1440' // 1 inch margins
    rtfContent += '\\sl480\\slmult1' // 1.5 line spacing for readability
    rtfContent += '\\f0\\fs24' // Default font: Times New Roman 12pt
    
    // Professional title page
    rtfContent += '\\qc\\sb2880\\sa1440' // Center with generous spacing
    rtfContent += '\\f1\\fs48\\b ' // Garamond 24pt bold
    rtfContent += escapeRtfText(query.toUpperCase())
    rtfContent += '\\b0\\par'
    
    // Subtitle if genre is specified
    if (novelConfig?.genre && novelConfig.genre !== 'Tidak ditentukan') {
      rtfContent += '\\sb720\\sa720\\f1\\fs28\\i '
      rtfContent += escapeRtfText(`Novel ${novelConfig.genre}`)
      rtfContent += '\\i0\\par'
    }
    
    rtfContent += '\\page' // Page break after title page
    
    // Table of contents with professional formatting
    rtfContent += '\\qc\\sb1440\\sa720\\f1\\fs32\\b DAFTAR ISI\\b0\\par\\par'
    rtfContent += '\\ql\\sb240\\sa120' // Left align with proper spacing
    
    const completedSections = outline.filter(section => generatedContent[section.id])
    completedSections.forEach((section, index) => {
      rtfContent += `\\f0\\fs24\\tqr\\tx8640 ${index + 1}. ${escapeRtfText(section.title)}\\tab ${index + 1}\\par`
    })
    rtfContent += '\\page' // Page break after TOC
    
    // Main content with professional chapter formatting
    completedSections.forEach((section, sectionIndex) => {
      const content = generatedContent[section.id]
      
      // Professional chapter header
      rtfContent += '\\qc\\sb1440\\sa720' // Center with generous spacing
      rtfContent += '\\f1\\fs36\\b ' // Garamond 18pt bold
      rtfContent += `BAB ${sectionIndex + 1}\\par`
      rtfContent += '\\sb360\\sa720\\f1\\fs28\\b '
      rtfContent += `${escapeRtfText(section.title.toUpperCase())}\\par`
      rtfContent += '\\b0\\ql\\f0\\fs24' // Reset to body text
      
      // Process content with professional formatting
      const processedContent = formatContentForRtf(content)
      rtfContent += processedContent
      
      // Add page break between chapters (except for the last one)
      if (sectionIndex < completedSections.length - 1) {
        rtfContent += '\\page'
      }
    })
    
    rtfContent += '}'
    
    // Create and download the file
    const blob = new Blob([rtfContent], { type: 'application/rtf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    
    // Generate clean filename
    const cleanTitle = query.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').toLowerCase()
    a.download = `${cleanTitle}-novel.doc`
    
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
  
  // Helper function to escape RTF special characters
  const escapeRtfText = (text) => {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/{/g, '\\{')
      .replace(/}/g, '\\}')
      .replace(/[\u0080-\uFFFF]/g, (match) => {
        return '\\u' + match.charCodeAt(0) + '?'
      })
  }
  
  // Helper function to clean LLM artifacts and format content professionally
  const cleanLLMContent = (content) => {
    if (!content) return ''
    
    let cleaned = content
    
    // Remove common LLM artifacts and meta-commentary with comprehensive patterns
    const llmPatterns = [
      // Remove meta-commentary about the story (enhanced patterns)
      /^(Tentu,?\s*)?(Berikut|Ini adalah|Dalam|Pada|Cerita ini|Novel ini|Kisah ini|Sekarang|Kali ini).*?[.:]/gmi,
      /^(Tentu,?\s*)?(Here is|This is|The story|In this|Now|This time).*?[.:]/gmi,
      /^(Tentu,?\s*)?(ini adalah|this is)\s+(Act|Bab|Chapter|Bagian).*?untuk novel.*?[.:]/gmi,
      
      // Remove specific LLM introductory phrases and task acknowledgments
      /^(Baik,?\s*)?(saya akan|I will)\s+(menuliskan|menulis|write|create).*?untuk novel.*?[.:]/gmi,
      /^(Baik,?\s*)?(saya akan|I will)\s+(menuliskan|menulis|write|create).*?(Plot Point|Bab|Chapter|Bagian).*?[.:]/gmi,
      /^(Baik,?\s*)?(saya akan|I will).*?(memenuhi|fulfill|meet).*?(persyaratan|requirements).*?[.:]/gmi,
      /^(Baik,?\s*)?(saya akan|I will).*?(berfokus|focus).*?(pada|on).*?[.:]/gmi,
      /^(Baik,?\s*)?(saya akan|I will).*?(target|mencapai|achieve).*?(jumlah kata|word count).*?[.:]/gmi,
      
      // Remove instructional phrases
      /^(Mari kita|Let's|Now let's|Sekarang mari|Ayo kita|Come on).*?[.:]/gmi,
      /^(Kita akan|We will|I will now|Saya akan|I'll).*?[.:]/gmi,
      
      // Remove transitional meta-text
      /^(Selanjutnya|Next|Kemudian|Then|Lalu|Setelah itu).*?(cerita|story|bagian|part).*?[.:]/gmi,
      /^(Bagian ini|This part|This section|Pada bagian ini|In this section).*?[.:]/gmi,
      
      // Remove explanatory phrases and parenthetical notes
      /\(.*?(menjelaskan|explaining|note|catatan|keterangan).*?\)/gi,
      /\[.*?(note|catatan|keterangan|penjelasan).*?\]/gi,
      
      // Remove chapter/section announcements and headers
      /^(Chapter|Bab|Bagian|Part)\s*\d+\s*[:-]?\s*/gmi,
      /^(Chapter|Bab|Bagian|Part)\s*[A-Z][a-z]*\s*[:-]?\s*/gmi,
      /^(Act|Babak)\s*[IVX]+\s*[:-]?.*?untuk novel.*?[.:]/gmi,
      
      // Remove author's notes, asides, and meta-commentary
      /^\*.*?\*$/gm,
      /^Note:.*$/gmi,
      /^Catatan:.*$/gmi,
      /^Author's note:.*$/gmi,
      /^Penulis:.*$/gmi,
      
      // Remove common LLM transitional phrases
      /^(Dengan demikian|Thus|Therefore|Oleh karena itu|Hence).*?[.:]/gmi,
      /^(Sebagai|As|Seperti|Like|Bagaikan).*?(contoh|example|ilustrasi|illustration).*?[.:]/gmi,
      
      // Remove meta-narrative commentary
      /^(Dalam konteks|In the context|Dalam hal ini|In this case).*?[.:]/gmi,
      /^(Perlu dicatat|It should be noted|Penting untuk|It's important to).*?[.:]/gmi,
      
      // Remove horizontal dividers and separators
      /^---+$/gm,
      /^===+$/gm,
      /^\*\*\*+$/gm,
      
      // Remove Roman numerals and numbering at end of chapters
      /\s*[IVX]+\.?\s*$/gmi,
      /\s*\d+\.?\s*$/gm,
      
      // Remove redundant spacing and formatting
      /\n\s*\n\s*\n+/g,
      /^\s+|\s+$/gm
    ]
    
    // Apply all cleaning patterns
    llmPatterns.forEach(pattern => {
      if (pattern.source.includes('\\n')) {
        cleaned = cleaned.replace(pattern, '\n\n')
      } else {
        cleaned = cleaned.replace(pattern, '')
      }
    })
    
    // Advanced cleaning for specific LLM artifacts
    // Remove sentences that start with common LLM phrases
    const sentencePatterns = [
      /^(Tentu,?\s*)?(Dalam cerita ini|In this story|Pada novel ini|In this novel).*?\./gmi,
      /^(Tentu,?\s*)?(Tokoh utama|The main character|Protagonis|The protagonist).*?(akan|will|sedang|is).*?\./gmi,
      /^(Tentu,?\s*)?(Cerita dimulai|The story begins|Novel ini dimulai|This novel starts).*?\./gmi,
      /^(Tentu,?\s*)?(Seperti yang telah|As previously|Sebagaimana|As).*?(disebutkan|mentioned|dijelaskan|explained).*?\./gmi,
      /^(Tentu,?\s*)?ini adalah\s+(Act|Bab|Chapter|Bagian).*?untuk novel.*?\./gmi,
      /^(Tentu,?\s*)?berikut adalah\s+(Act|Bab|Chapter|Bagian).*?\./gmi,
      
      // Remove long introductory sentences with task descriptions
      /^(Baik,?\s*)?(saya akan|I will)\s+(menuliskan|menulis|write|create).*?\.(\s*Saya akan.*?\.)*/gmi,
      /^.*?(memenuhi semua persyaratan|meet all requirements).*?(termasuk|including).*?(target jumlah kata|word count target).*?\./gmi,
      /^.*?(berfokus pada|focus on).*?(detail|deskripsi|description).*?(mendalam|deep).*?\./gmi,
      /^.*?(dialog yang autentik|authentic dialogue).*?(konflik|conflict).*?(pengembangan karakter|character development).*?\./gmi,
      /^.*?(untuk mencapai|to achieve).*?(panjang yang diinginkan|desired length).*?\./gmi,
      
      // Remove very specific LLM task acknowledgment patterns
      /^Baik,\s*saya akan menuliskan Plot Point.*?untuk novel.*?\./gmi,
      /^.*?dengan memenuhi semua persyaratan yang diberikan.*?\./gmi,
      /^.*?Saya akan berfokus pada.*?untuk mencapai panjang yang diinginkan.*?\./gmi
    ]
    
    sentencePatterns.forEach(pattern => {
      cleaned = cleaned.replace(pattern, '')
    })
    
    // Remove complex multi-sentence introductory blocks
    const blockPatterns = [
      // Remove entire blocks that start with task acknowledgment and contain requirements
      /^(Baik,?\s*)?(saya akan|I will)\s+(menuliskan|menulis|write|create).*?Plot Point.*?novel.*?dengan memenuhi.*?persyaratan.*?\.(\s*.*?)*?---\s*$/gmi,
      /^(Baik,?\s*)?(saya akan|I will).*?memenuhi.*?persyaratan.*?termasuk.*?target.*?kata.*?\.(\s*.*?)*?---\s*$/gmi,
      /^.*?Plot Point.*?untuk novel.*?dengan memenuhi.*?persyaratan.*?\.(\s*.*?)*?---\s*$/gmi,
      // Remove blocks that mention word count targets and requirements
      /^.*?(target jumlah kata|word count).*?(\d+[.,]?\d*[-–]\d+[.,]?\d*).*?kata.*?\.(\s*.*?)*?---\s*$/gmi,
      /^.*?(berfokus pada|focus on).*?(detail|deskripsi|description).*?(mendalam|deep).*?(dialog|konflik|pengembangan karakter).*?\.(\s*.*?)*?---\s*$/gmi,
      // Remove any remaining horizontal dividers with preceding text
      /^.*?---+\s*$/gm,
      
      // Remove specific long introductory paragraphs (more aggressive)
      /^Baik,\s*saya akan menuliskan.*?Plot Point.*?novel.*?\.(\s*Saya akan.*?)*?---$/gmi,
      /^.*?memenuhi semua persyaratan yang diberikan.*?\.(\s*.*?)*?---$/gmi,
      /^.*?termasuk target jumlah kata.*?\.(\s*.*?)*?---$/gmi
    ]
    
    blockPatterns.forEach(pattern => {
      cleaned = cleaned.replace(pattern, '')
    })
    
    // Additional aggressive cleaning for entire paragraphs containing LLM artifacts
    const lines = cleaned.split('\n')
    const filteredLines = []
    let skipNextLines = 0
    
    for (let i = 0; i < lines.length; i++) {
      if (skipNextLines > 0) {
        skipNextLines--
        continue
      }
      
      const line = lines[i].trim()
      
      // Skip lines that contain LLM task acknowledgments
      if (
        line.match(/^Baik,\s*saya akan menuliskan.*?Plot Point/i) ||
        line.match(/^.*?dengan memenuhi semua persyaratan/i) ||
        line.match(/^.*?termasuk target jumlah kata/i) ||
        line.match(/^.*?Saya akan berfokus pada/i) ||
        line.match(/^.*?untuk mencapai panjang yang diinginkan/i) ||
        line.match(/^---+$/)
      ) {
        // Skip this line and potentially the next few lines if they're part of the same paragraph
        skipNextLines = 2
        continue
      }
      
      filteredLines.push(lines[i])
    }
    
    cleaned = filteredLines.join('\n')
    
    // Clean up dialogue formatting for consistency
    cleaned = cleaned.replace(/^["'](.+?)["']$/gm, '"$1"')
    
    // Remove empty paragraphs and normalize spacing
    cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n')
    cleaned = cleaned.replace(/^\s*\n+|\n+\s*$/g, '')
    
    // Final cleanup: remove any remaining single-word or very short paragraphs that are likely artifacts
    const paragraphs = cleaned.split(/\n\s*\n/)
    const cleanedParagraphs = paragraphs.filter(para => {
      const trimmed = para.trim()
      // Keep paragraphs that are substantial or are dialogue
      return trimmed.length > 30 || trimmed.match(/^["'].*["']$/) || trimmed.match(/^[A-Z][a-z]+:/) // Keep dialogue or character names
    })
    
    // Create proper paragraph structure for professional novel formatting
    const mergedParagraphs = []
    let currentParagraph = ''
    let sentenceCount = 0
    
    cleanedParagraphs.forEach((para, index) => {
      const trimmed = para.trim()
      
      // Count sentences in current paragraph
      const sentences = trimmed.split(/[.!?]+/).filter(s => s.trim().length > 10)
      
      // If it's dialogue, keep it separate
      if (trimmed.match(/^["'].*["']$/)) {
        if (currentParagraph) {
          mergedParagraphs.push(currentParagraph.trim())
          currentParagraph = ''
          sentenceCount = 0
        }
        mergedParagraphs.push(trimmed)
        return
      }
      
      // Build paragraphs with 3-5 sentences for optimal readability
      if (sentenceCount === 0) {
        currentParagraph = trimmed
        sentenceCount = sentences.length
      } else if (sentenceCount + sentences.length <= 5) {
        currentParagraph += ' ' + trimmed
        sentenceCount += sentences.length
      } else {
        // Current paragraph is complete, start new one
        mergedParagraphs.push(currentParagraph.trim())
        currentParagraph = trimmed
        sentenceCount = sentences.length
      }
      
      // Force paragraph break if we have enough content
      if (sentenceCount >= 3 && currentParagraph.length >= 300) {
        mergedParagraphs.push(currentParagraph.trim())
        currentParagraph = ''
        sentenceCount = 0
      }
    })
    
    // Add any remaining paragraph
    if (currentParagraph.trim()) {
      mergedParagraphs.push(currentParagraph.trim())
    }
    
    // Ensure each page has 2-3 substantial paragraphs
    const finalParagraphs = []
    let pageBuffer = []
    
    mergedParagraphs.forEach(para => {
      pageBuffer.push(para)
      
      // If we have 2-3 paragraphs, that's a good page
      if (pageBuffer.length >= 2) {
        const totalWords = pageBuffer.join(' ').split(/\s+/).length
        
        // Aim for 250-400 words per page
        if (totalWords >= 250 || pageBuffer.length >= 3) {
          finalParagraphs.push(...pageBuffer)
          pageBuffer = []
        }
      }
    })
    
    // Add remaining paragraphs
    if (pageBuffer.length > 0) {
      finalParagraphs.push(...pageBuffer)
    }
    
    cleaned = finalParagraphs.join('\n\n')
    
    return cleaned.trim()
  }
  
  // Helper function to format content with proper paragraphs and styling
  const formatContentForRtf = (content) => {
    if (!content) return ''
    
    // First clean the content of LLM artifacts
    let processedContent = cleanLLMContent(content)
    
    // Remove markdown headers and convert to proper formatting
    processedContent = processedContent.replace(/^#{1,6}\s+(.+)$/gm, (match, title) => {
      return `**${title.trim()}**` // Convert headers to bold text
    })
    
    // Split content into paragraphs and process each one
    const paragraphs = processedContent.split(/\n\s*\n/)
    let formattedContent = ''
    
    paragraphs.forEach((paragraph, index) => {
      if (paragraph.trim()) {
        // Clean up the paragraph
        let cleanParagraph = paragraph.trim()
        
        // Skip if it's likely an LLM artifact that wasn't caught
         if (cleanParagraph.match(/^(Tentu,?\s*)?(Berikut|Ini adalah|Here is|This is|Mari kita|Let's|Dalam cerita|In this story|Tokoh utama|The main character|ini adalah\s+(Act|Bab|Chapter))/i)) {
           return // Skip this paragraph
         }
         
         // Skip Roman numerals and chapter endings
         if (cleanParagraph.match(/^\s*[IVX]+\.?\s*$/) || cleanParagraph.match(/^\s*\d+\.?\s*$/)) {
           return // Skip numbering artifacts
         }
         
         // Skip very short paragraphs that are likely artifacts (unless they're dialogue)
         if (cleanParagraph.length < 50 && !cleanParagraph.match(/^["'].*["']$/)) {
           return // Skip short non-dialogue paragraphs
         }
         
         // Handle different types of content with professional formatting
         if (cleanParagraph.startsWith('**') && cleanParagraph.endsWith('**')) {
           // Bold text (subheadings) - centered with elegant spacing
           const boldText = cleanParagraph.slice(2, -2)
           formattedContent += `\\par\\sb720\\sa720\\qc\\f1\\fs32\\b ${escapeRtfText(boldText)}\\b0\\f0\\fs24\\ql\\par\\par`
         } else if (cleanParagraph.startsWith('*') && cleanParagraph.endsWith('*') && !cleanParagraph.startsWith('**')) {
           // Italic text (emphasis) - centered with subtle styling
           const italicText = cleanParagraph.slice(1, -1)
           formattedContent += `\\par\\sb480\\sa480\\qc\\f0\\fs24\\i ${escapeRtfText(italicText)}\\i0\\ql\\par\\par`
         } else if (cleanParagraph.match(/^["'].*["']$/)) {
           // Dialog - professional formatting with proper indentation and spacing
           formattedContent += `\\par\\sb360\\sa360\\fi1440\\ql ${escapeRtfText(cleanParagraph)}\\par\\par`
         } else if (cleanParagraph.match(/^[A-Z][a-z]+\s*:/)) {
           // Character names or scene headers - special formatting
           formattedContent += `\\par\\sb480\\sa240\\ql\\f0\\fs24\\b ${escapeRtfText(cleanParagraph)}\\b0\\par`
         } else {
           // Regular narrative paragraph with professional novel formatting
           // Remove any remaining markdown formatting
           cleanParagraph = cleanParagraph.replace(/\*\*(.*?)\*\*/g, '\\b $1\\b0') // Convert bold markdown to RTF
           cleanParagraph = cleanParagraph.replace(/\*(.*?)\*/g, '\\i $1\\i0') // Convert italic markdown to RTF
           cleanParagraph = cleanParagraph.replace(/`(.*?)`/g, '$1') // Remove code markdown
           
           // Professional novel paragraph formatting with clear paragraph breaks
           // Add substantial spacing between paragraphs and proper indentation
           if (index === 0) {
             // First paragraph - no indent, but clear spacing
             formattedContent += `\\par\\sb480\\sa480\\ql ${escapeRtfText(cleanParagraph)}\\par\\par`
           } else {
             // Subsequent paragraphs - first-line indent with clear paragraph breaks
             formattedContent += `\\par\\sb480\\sa480\\fi1440\\ql ${escapeRtfText(cleanParagraph)}\\par\\par`
           }
         }
      }
    })
    
    return formattedContent
  }

  // Handle section preview
  const handlePreviewSection = (section) => {
    setSelectedSection(section)
    setShowPreview(true)
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border-primary sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className={cn(buttonVariants.secondary, "flex items-center")}
                disabled={isGenerating}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Kembali
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <BookOpen className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-text-primary">Novel Agent</h1>
                  <p className="text-sm text-text-secondary">Hasil generasi novel</p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              {completedSections > 0 && (
                <>
                  <button
                    onClick={handleCopyAll}
                    className={cn(
                      buttonVariants.secondary,
                      "flex items-center",
                      copiedSection === 'all' && "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                    )}
                  >
                    {copiedSection === 'all' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Tersalin
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Salin Semua
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDownload}
                    className={cn(buttonVariants.primary, "flex items-center")}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Topic and Config Display */}
        <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Detail Novel</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-text-tertiary mb-2">Topik</h3>
              <p className="text-text-primary break-words">"<span className="font-medium">{query}</span>"</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-text-tertiary">Genre:</span>
                <p className="font-medium text-text-primary">{novelConfig?.genre}</p>
              </div>
              <div>
                <span className="text-text-tertiary">Gaya:</span>
                <p className="font-medium text-text-primary">{novelConfig?.writingStyle}</p>
              </div>
              <div>
                <span className="text-text-tertiary">Struktur:</span>
                <p className="font-medium text-text-primary">{novelConfig?.plotStructure}</p>
              </div>
              <div>
                <span className="text-text-tertiary">Bahasa:</span>
                <p className="font-medium text-text-primary">{novelConfig?.language}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary flex items-center">
              <Clock className="w-5 h-5 mr-2 text-purple-500" />
              Progress Generasi
              {isGenerating && (
                <span className="ml-3 px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/20">
                  Sedang Berjalan
                </span>
              )}
            </h3>
            
            {/* Generation Status */}
            {!isGenerating && completedSections < totalSections && (
              <button
                onClick={onContinueGeneration}
                className={cn(buttonVariants.primary, "flex items-center")}
              >
                <Play className="w-4 h-4 mr-2" />
                {completedSections === 0 ? 'Mulai Generasi' : 'Lanjutkan Generasi'}
              </button>
            )}
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-text-secondary">
                {completedSections} dari {totalSections} bagian selesai
              </span>
              <span className="text-text-secondary">
                {Math.round(progressPercentage)}%
              </span>
            </div>
            <div className="w-full bg-bg-tertiary rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
          
          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            <div>
              <span className="text-text-tertiary">Kata Tertulis:</span>
              <p className="font-medium text-text-primary">{totalWords.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-text-tertiary">Target Kata:</span>
              <p className="font-medium text-text-primary">{estimatedTotalWords.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-text-tertiary">Estimasi Halaman:</span>
              <p className="font-medium text-text-primary">{Math.ceil(totalWords / 275)}</p>
            </div>
            <div>
              <span className="text-text-tertiary">Sisa Waktu:</span>
              <p className="font-medium text-text-primary">{remainingTime || 'Belum dapat diestimasi'}</p>
            </div>
          </div>
          
          {/* Word Target Achievement Statistics */}
          {completedSections > 0 && (
            <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
              <h4 className="text-sm font-medium text-text-primary mb-3">Pencapaian Target Kata:</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {(() => {
                  const sectionsWithTarget = outline.filter(section => {
                    const content = generatedContent[section.id]
                    if (!content) return false
                    
                    const wordCount = calculateWordCount(content)
                    const minWords = section.minWords || 8000
                    const maxWords = section.maxWords || 15000
                    return wordCount >= minWords && wordCount <= maxWords
                  }).length
                  
                  const sectionsUnderTarget = outline.filter(section => {
                    const content = generatedContent[section.id]
                    if (!content) return false
                    
                    const wordCount = calculateWordCount(content)
                    const minWords = section.minWords || 8000
                    return wordCount < minWords
                  }).length
                  
                  const sectionsOverTarget = outline.filter(section => {
                    const content = generatedContent[section.id]
                    if (!content) return false
                    
                    const wordCount = calculateWordCount(content)
                    const maxWords = section.maxWords || 15000
                    return wordCount > maxWords
                  }).length
                  
                  const targetAchievementRate = completedSections > 0 ? Math.round((sectionsWithTarget / completedSections) * 100) : 0
                  
                  return (
                    <>
                      <div>
                        <span className="text-emerald-400">Target Tercapai:</span>
                        <p className="font-medium text-text-primary">{sectionsWithTarget} bagian</p>
                      </div>
                      <div>
                        <span className="text-yellow-400">Kurang Target:</span>
                        <p className="font-medium text-text-primary">{sectionsUnderTarget} bagian</p>
                      </div>
                      <div>
                        <span className="text-blue-400">Melebihi Target:</span>
                        <p className="font-medium text-text-primary">{sectionsOverTarget} bagian</p>
                      </div>
                      <div>
                        <span className="text-text-tertiary">Tingkat Pencapaian:</span>
                        <p className="font-medium text-text-primary">{targetAchievementRate}%</p>
                      </div>
                    </>
                  )
                })()
                }
              </div>
            </div>
          )}
          
          {/* Section Progress Details */}
          {outline && outline.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-text-primary mb-3">Progress Detail Bagian:</h4>
              <div className="space-y-2">
                {outline.map((section, index) => {
                  const isCompleted = generatedContent && generatedContent[section.id]
                  const isCurrent = isGenerating && currentSection === section.id
                  const sectionWordCount = isCompleted ? calculateWordCount(generatedContent[section.id]) : 0
                  const minWords = section.minWords || 8000
                  const maxWords = section.maxWords || 15000
                  const targetWords = Math.round((minWords + maxWords) / 2)
                  const sectionProgress = isCompleted ? 100 : (isCurrent ? 50 : 0)
                  
                  // Determine word count achievement status
                  const getWordAchievementStatus = () => {
                    if (!isCompleted) return { text: '', color: '' }
                    
                    if (sectionWordCount >= minWords && sectionWordCount <= maxWords) {
                      return { text: '✓ Target tercapai', color: 'text-emerald-400' }
                    } else if (sectionWordCount < minWords) {
                      return { text: '⚠ Kurang target', color: 'text-yellow-400' }
                    } else {
                      return { text: '↗ Melebihi target', color: 'text-blue-400' }
                    }
                  }
                  
                  const wordAchievement = getWordAchievementStatus()
                  
                  return (
                    <div key={section.id} className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg border border-border-secondary">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                          isCompleted ? "bg-emerald-500 text-white" :
                          isCurrent ? "bg-yellow-500 text-white" :
                          "bg-gray-500 text-white"
                        )}>
                          {isCompleted ? '✓' : index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-text-primary">{section.title}</span>
                            <div className="flex items-center space-x-4 text-xs text-text-secondary">
                              <span>
                                {sectionWordCount.toLocaleString()} / {minWords.toLocaleString()}-{maxWords.toLocaleString()} kata ({Math.ceil(sectionWordCount / 275)} hal)
                              </span>
                              {wordAchievement.text && (
                                <span className={cn("text-xs font-medium", wordAchievement.color)}>
                                  {wordAchievement.text}
                                </span>
                              )}
                              <span className={cn(
                                "px-2 py-1 rounded-full",
                                isCompleted ? "bg-emerald-500/10 text-emerald-400" :
                                isCurrent ? "bg-yellow-500/10 text-yellow-400" :
                                "bg-gray-500/10 text-gray-400"
                              )}>
                                {isCompleted ? 'Selesai' : isCurrent ? 'Sedang Ditulis' : 'Menunggu'}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <div className="w-full bg-bg-primary rounded-full h-1.5">
                              <div 
                                className={cn(
                                  "h-1.5 rounded-full transition-all duration-300",
                                  isCompleted ? "bg-emerald-500" :
                                  isCurrent ? "bg-yellow-500" :
                                  "bg-gray-500"
                                )}
                                style={{ width: `${sectionProgress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {isCurrent && (
                        <div className="ml-3">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          
          {/* Current Section */}
          {isGenerating && currentSection && (
            <div className="mt-4 p-4 bg-bg-tertiary rounded-lg border border-border-secondary">
              <div className="flex items-center space-x-2 mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                <span className="text-sm font-medium text-text-primary">Sedang menulis:</span>
              </div>
              <p className="text-text-secondary text-sm">{currentSection}</p>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-8">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-400 font-medium">Error</span>
            </div>
            <p className="text-red-300 mt-2">{error}</p>
          </div>
        )}

        {/* Generated Content */}
        {generatedContent?.length > 0 && (
          <div className="bg-bg-secondary rounded-xl p-6 border border-border-primary">
            <h3 className="text-lg font-semibold text-text-primary mb-6 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-purple-500" />
              Konten Novel
            </h3>
            
            <div className="space-y-6">
              {outline.map((outlineSection, index) => {
                const content = generatedContent[outlineSection.id]
                const section = {
                  ...outlineSection,
                  content: content || '',
                  isGenerated: !!content
                }
                return (
                  <NovelSection
                    key={outlineSection.id}
                    section={section}
                    index={index}
                    onCopy={handleCopySection}
                    onPreview={handlePreviewSection}
                    onRegenerate={onRegenerateSection}
                    copiedSection={copiedSection}
                    isGenerating={isGenerating}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!generatedContent || Object.keys(generatedContent).length === 0) && !isGenerating && (
          <div className="bg-bg-secondary rounded-xl p-12 border border-border-primary text-center">
            <BookOpen className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">Belum Ada Konten</h3>
            <p className="text-text-secondary mb-6">Mulai generasi untuk membuat novel Anda</p>
            {onContinueGeneration && (
              <button
                onClick={onContinueGeneration}
                className={cn(buttonVariants.primary, "flex items-center mx-auto")}
              >
                <Play className="w-4 h-4 mr-2" />
                Mulai Generasi
              </button>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && selectedSection && (
        <PreviewModal
          section={selectedSection}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  )
}

// Novel Section Component
const NovelSection = ({ section, index, onCopy, onPreview, onRegenerate, copiedSection, isGenerating }) => {
  const wordCount = calculateWordCount(section.content)
  const parsedContent = parseMarkdownContent(section.content)
  
  // Get target word counts from section
  const minWords = section.minWords || 8000
  const maxWords = section.maxWords || 15000
  
  // Determine word count status
  const getWordCountStatus = () => {
    if (!section.content) return { status: 'pending', color: 'gray', text: 'Belum ditulis' }
    
    if (wordCount >= minWords && wordCount <= maxWords) {
      return { status: 'success', color: 'emerald', text: 'Target tercapai' }
    } else if (wordCount < minWords) {
      return { status: 'warning', color: 'yellow', text: 'Kurang dari target' }
    } else {
      return { status: 'info', color: 'blue', text: 'Melebihi target' }
    }
  }
  
  const wordStatus = getWordCountStatus()
  
  return (
    <div className="bg-bg-tertiary rounded-lg p-6 border border-border-secondary">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <span className="text-sm font-medium text-purple-400">Bagian {index + 1}</span>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-text-tertiary">
                {wordCount.toLocaleString()} kata
              </span>
              <span className="text-xs text-text-tertiary">•</span>
              <span className="text-xs text-text-tertiary">
                Target: {minWords.toLocaleString()}-{maxWords.toLocaleString()}
              </span>
              <span className={cn(
                "text-xs px-2 py-1 rounded-full",
                `bg-${wordStatus.color}-500/10 text-${wordStatus.color}-400`
              )}>
                {wordStatus.text}
              </span>
            </div>
            {section.status && (
              <span className={cn(
                "text-xs px-2 py-1 rounded-full",
                section.status === 'completed' ? "bg-emerald-500/10 text-emerald-400" :
                section.status === 'generating' ? "bg-yellow-500/10 text-yellow-400" :
                "bg-gray-500/10 text-gray-400"
              )}>
                {section.status === 'completed' ? 'Selesai' :
                 section.status === 'generating' ? 'Sedang Ditulis' : 'Menunggu'}
              </span>
            )}
          </div>
          <h4 className="text-text-primary font-semibold text-lg mb-3">{section.title}</h4>
        </div>
        
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => onPreview(section)}
            className="p-2 text-text-tertiary hover:text-text-secondary hover:bg-bg-primary rounded-lg transition-colors"
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => onCopy(section.id || index, section.content)}
            className={cn(
              "p-2 rounded-lg transition-colors",
              copiedSection === (section.id || index)
                ? "text-emerald-400 bg-emerald-500/10"
                : "text-text-tertiary hover:text-text-secondary hover:bg-bg-primary"
            )}
            title="Salin"
          >
            {copiedSection === (section.id || index) ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
          {onRegenerate && (
            <button
              onClick={() => onRegenerate(section.id || index)}
              disabled={isGenerating}
              className={cn(
                "p-2 rounded-lg transition-colors",
                isGenerating
                  ? "text-text-tertiary opacity-50 cursor-not-allowed"
                  : "text-text-tertiary hover:text-text-secondary hover:bg-bg-primary"
              )}
              title="Regenerate"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Content Preview */}
      <div className="prose prose-invert max-w-none">
        <div className="text-text-secondary text-sm leading-relaxed line-clamp-6 whitespace-pre-line">
          {parsedContent.preview}
        </div>
        {parsedContent.hasMore && (
          <button
            onClick={() => onPreview(section)}
            className="text-purple-400 hover:text-purple-300 text-sm mt-2 flex items-center"
          >
            Lihat selengkapnya
            <Eye className="w-4 h-4 ml-1" />
          </button>
        )}
      </div>
    </div>
  )
}

// Preview Modal Component
const PreviewModal = ({ section, onClose }) => {
  const wordCount = calculateWordCount(section.content)
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-secondary rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-border-primary">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-primary">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{section.title}</h3>
            <p className="text-sm text-text-tertiary">{wordCount.toLocaleString()} kata</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-tertiary hover:text-text-secondary hover:bg-bg-tertiary rounded-lg transition-colors"
          >
            <EyeOff className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="prose prose-invert max-w-none">
            <div className="text-text-secondary leading-relaxed whitespace-pre-line">
              {parseMarkdownContent(section.content).parsedContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

NovelSection.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onCopy: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onRegenerate: PropTypes.func,
  copiedSection: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isGenerating: PropTypes.bool.isRequired
}

PreviewModal.propTypes = {
  section: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
}

NovelResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  novelConfig: PropTypes.object.isRequired,
  generatedContent: PropTypes.object.isRequired,
  currentSection: PropTypes.string,
  isGenerating: PropTypes.bool.isRequired,
  progress: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onContinueGeneration: PropTypes.func,
  onRegenerateSection: PropTypes.func,
  error: PropTypes.string,
  startTime: PropTypes.number
}

export default NovelResultPage